/**
 * ChatV2 - Main Chat Component using Vercel AI SDK
 *
 * This is the entry point for the new chat UI.
 * Guarded by VITE_USE_VERCEL_AI_CHAT feature flag.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { ChatLayout } from './layout/ChatLayout'
import { ChatSidebar } from './sidebar/ChatSidebar'
import { WelcomeScreen } from './main/WelcomeScreen'
import { MessageList } from './messages/MessageList'
import { ChatInput } from './input/ChatInput'
import { ErrorBoundary } from './common/ErrorBoundary'
import { KeyboardShortcutsModal } from './common/KeyboardShortcutsModal'
import { NetworkStatus } from './common/NetworkStatus'
import type { AgentId, ContextType } from '../types'
import { useChatV2 } from '../hooks/useChatV2'
import { useConversationsV2 } from '../hooks/useConversationsV2'
import { useChatV2Store } from '../hooks/useChatV2Store'
import { useSpeechOutput } from '../hooks/useSpeechOutput'
import { useChatSearch } from '../hooks/useChatSearch'
import { addMessageFeedback } from '../api/chatApiV2'
import { AGENTS, QUICK_ACTIONS } from '../constants'
import { useAppStore } from '@/store'
import { useTranslation } from '@/i18n'
import { getCurrentDirection } from '@/i18n/config'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import DocumentsModal from '@/routes/IntelligentBoard/DocumentsModal'
import { Share2 } from 'lucide-react'
import ShareModal from '@/components/ShareModal'
import { isFeatureAllowedInEmbed } from '@/lib/embedFeatures'

/**
 * Map agent ID from app store to ChatV2 agent ID
 */
function mapAppStoreAgentToAgentId(agentId: string | undefined): AgentId | null {
  if (!agentId) return null
  const mapping: Record<string, AgentId> = {
    'plant-support': 'pam',
    'chemical-agent': 'aice',
    'mechanical-agent': 'max',
    'electrical-agent': 'ella',
    // Also support if the ID is already in ChatV2 format
    'pam': 'pam',
    'aice': 'aice',
    'max': 'max',
    'ella': 'ella',
  }
  return mapping[agentId] || null
}

interface Board {
  id: string
  name: string
}

/**
 * Sanitize error messages for user display
 * Hides technical details while providing useful feedback
 */
function getUserFriendlyError(
  error: Error,
  t: (key: string, opts: Record<string, unknown>) => string,
): string {
  const msg = error.message.toLowerCase()

  if (msg.includes('failed to send')) {
    return t('chatv2:errors.unable_send', { defaultValue: 'Unable to send message. Please try again.' })
  }
  if (msg.includes('failed to load')) {
    return t('chatv2:errors.unable_load', { defaultValue: 'Unable to load conversation. Please refresh the page.' })
  }
  if (msg.includes('failed to regenerate')) {
    return t('chatv2:errors.unable_regenerate', { defaultValue: 'Unable to regenerate response. Please try again.' })
  }
  if (msg.includes('failed to edit')) {
    return t('chatv2:errors.unable_edit', { defaultValue: 'Unable to edit message. Please try again.' })
  }
  if (msg.includes('401') || msg.includes('unauthorized')) {
    return t('chatv2:errors.session_expired_refresh', { defaultValue: 'Session expired. Please refresh the page to continue.' })
  }
  if (msg.includes('403') || msg.includes('forbidden')) {
    return t('chatv2:errors.forbidden', { defaultValue: 'You do not have permission to perform this action.' })
  }
  if (msg.includes('404') || msg.includes('not found')) {
    return t('chatv2:errors.not_found', { defaultValue: 'The requested resource could not be found.' })
  }
  if (msg.includes('network') || msg.includes('fetch')) {
    return t('chatv2:errors.network_retry', { defaultValue: 'Network error. Please check your connection and try again.' })
  }
  if (msg.includes('timeout')) {
    return t('chatv2:errors.timeout', { defaultValue: 'Request timed out. Please try again.' })
  }

  return t('chatv2:errors.generic_retry', { defaultValue: 'Something went wrong. Please try again.' })
}

interface ChatV2Props {
  contextId: string
  contextType: ContextType
  boardList?: Board[]
  onBoardChange?: (boardId: string) => void
  autoSendMessage?: boolean
  navigationKey?: string
  initialMessage?: string
  /**
   * FS-663: conversation to open on mount, from a shared chat link (`?c=`).
   * Applied once, before the reset effect below can clear it.
   */
  initialConversationId?: string
}

export function ChatV2({
  contextId,
  contextType,
  boardList,
  onBoardChange,
  autoSendMessage,
  navigationKey,
  initialMessage,
  initialConversationId,
}: ChatV2Props) {
  const { t } = useTranslation()
  // Local state
  const [showShortcutsModal, setShowShortcutsModal] = useState(false)
  // FS-663: share-this-conversation dialog.
  const [showShareModal, setShowShareModal] = useState(false)
  const [showDocumentsModal, setShowDocumentsModal] = useState(false)

  // Store state
  const {
    isSidebarCollapsed,
    setIsSidebarCollapsed,
    activeAgent,
    setActiveAgent,
    entireDataCatalog,
    setEntireDataCatalog,
    setCurrentConversationId,
  } = useChatV2Store()

  // Get user, documents, and selected agent from app store
  const { user, selectedDocuments, selectDocument, documents, loadBoard, board, selectedAgent: appStoreSelectedAgent, agents: appStoreAgents, isAgentsLoading } = useAppStore()
  const userName = user?.given_name || user?.name?.split(' ')[0] || undefined

  // Compute available ChatV2 agent IDs from the app store's tenant-filtered agents
  // undefined = still loading (downstream shows loading state)
  // [] = loaded but no agents enabled for this tenant (downstream shows nothing)
  const availableAgentIds = useMemo(() => {
    if (isAgentsLoading) return undefined
    if (!appStoreAgents || appStoreAgents.length === 0) return []
    return appStoreAgents
      .map((a) => mapAppStoreAgentToAgentId(a.id))
      .filter((id): id is AgentId => id !== null)
  }, [appStoreAgents, isAgentsLoading])

  // Load board when contextId changes (needed for documents modal to work)
  useEffect(() => {
    if (contextId && (!board || board.id !== contextId)) {
      loadBoard(contextId)
    }
  }, [contextId, board, loadBoard])

  // Sync selected agent from app store to ChatV2 store
  // This ensures agent selection from IntelligentBoard is preserved
  const hasSyncedAgentRef = useRef(false)
  useEffect(() => {
    if (appStoreSelectedAgent?.id && !hasSyncedAgentRef.current) {
      const chatV2AgentId = mapAppStoreAgentToAgentId(appStoreSelectedAgent.id)
      if (chatV2AgentId && chatV2AgentId !== activeAgent) {
        setActiveAgent(chatV2AgentId)
      }
      hasSyncedAgentRef.current = true
    }
  }, [appStoreSelectedAgent, activeAgent, setActiveAgent])

  // Process selected documents with names for display
  const selectedDocsWithNames = useMemo(() => {
    if (!selectedDocuments || !Array.isArray(selectedDocuments)) {
      return []
    }
    return selectedDocuments.map((doc: { document_id: string; doc_type?: string }) => {
      const fullDoc = documents.find((d: { id: string; name: string }) => d.id === doc.document_id)
      return {
        document_id: doc.document_id,
        name: fullDoc?.name || 'Document',
        doc_type: doc.doc_type,
      }
    })
  }, [selectedDocuments, documents])

  // Handle removing a document from selection - save immediately
  const handleRemoveDocument = useCallback((documentId: string) => {
    selectDocument(documentId, false, false, true) // isSelected=false, isConnector=false, shouldSave=true
  }, [selectDocument])

  // Handle newly uploaded document - add to board selection so AI can access it
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDocumentUploaded = useCallback((documentId: string, _docType: string) => {
    selectDocument(documentId, true, false, true) // isSelected=true, isConnector=false, shouldSave=true
  }, [selectDocument])

  // Speech output hook
  const {
    audioEnabled,
    setAudioEnabled,
    isSpeaking,
    speak,
    stop: stopSpeaking,
    isAvailable: isSpeechAvailable,
  } = useSpeechOutput()

  // Conversations hook
  const {
    conversations,
    currentConversationId,
    isLoading: isLoadingConversations,
    selectConversation,
    startNewChat,
    deleteConversation,
  } = useConversationsV2({ contextId })

  // Search state and hook
  const [searchQuery, setSearchQuery] = useState('')
  const { filteredConversations, isSearching, searchStats } = useChatSearch({
    conversations,
    searchQuery,
    enableDeepSearch: true, // Enable search through all message content
  })

  // Chat hook
  const {
    messages,
    input,
    setInput,
    send,
    stop,
    reload,
    isLoading: isChatLoading,
    isLoadingMessages,
    error,
    reset: resetChat,
    editMessage,
    orchestrationStatus,
    toolProgress,
    updateMessageFeedback,
    navigateVersion,
    getVersionInfo,
    respondToConsent,
  } = useChatV2({
    contextId,
    contextType,
    conversationId: currentConversationId || undefined,
    onError: (err) => {
      console.error('Chat error:', err)
    },
  })

  // FS-663: open the conversation a share link pointed at.
  //
  // Must run BEFORE the reset effect below, which clears the chat whenever
  // `currentConversationId` is null — on first mount that is exactly the state
  // a shared link arrives in, so selecting later would be undone immediately.
  const sharedConversationAppliedRef = useRef(false)
  useLayoutEffect(() => {
    if (!initialConversationId || sharedConversationAppliedRef.current) return
    sharedConversationAppliedRef.current = true
    // Set the id on the store DIRECTLY. `selectConversation` from
    // useConversationsV2 takes a Conversation OBJECT and reads `.id` off it, so
    // handing it a string silently sets `undefined` and the shared chat never
    // opens — and tsc does not catch it (greptile on #27485). We only have the
    // id here; the conversation itself has not loaded yet.
    setCurrentConversationId(initialConversationId)
  }, [initialConversationId, setCurrentConversationId])

  // Reset chat when starting new conversation
  useEffect(() => {
    // Don't wipe a conversation we are in the middle of restoring from a link.
    if (initialConversationId && !sharedConversationAppliedRef.current) return
    if (!currentConversationId) {
      resetChat()
    }
  }, [currentConversationId, resetChat, initialConversationId])

  // Auto-send from navigation refs
  const autoSendTriggeredRef = useRef(false)
  const autoSendTimerRef = useRef<NodeJS.Timeout | null>(null)
  const lastContextIdRef = useRef<string | null>(null)
  const lastNavigationKeyRef = useRef<string | null>(null)
  // Track if auto-send caused the conversation reset (to prevent re-triggering)
  const autoSendCausedResetRef = useRef(false)
  // Track whether we've already applied the initialMessage prop to input for this navigation
  const initialMessageAppliedRef = useRef(false)

  // Reset auto-send trigger when context/navigation changes
  // NOTE: We no longer reset on conversation reset because auto-send itself
  // calls startNewChat() which resets the conversation, causing a loop
  useEffect(() => {
    const contextChanged = contextId !== lastContextIdRef.current
    const normalizedNavKey = navigationKey?.trim() || null
    const navigationChanged = normalizedNavKey !== lastNavigationKeyRef.current && normalizedNavKey !== null

    if (contextChanged || navigationChanged) {
      // Only reset if this change wasn't caused by auto-send itself
      if (!autoSendCausedResetRef.current) {
        autoSendTriggeredRef.current = false
      }
      autoSendCausedResetRef.current = false
      // Allow seeding input from a new navigation/context (if an initialMessage is provided)
      initialMessageAppliedRef.current = false
      lastContextIdRef.current = contextId || null
      lastNavigationKeyRef.current = normalizedNavKey
    }
  }, [contextId, navigationKey])

  // Set initial message from navigation prop.
  //
  // IMPORTANT: This effect must run *after* the context/navigation reset effect above.
  // On initial mount, the reset effect runs (lastContextIdRef starts as null), and we
  // want to allow seeding exactly once after that reset, not have the reset clobber
  // the "applied" flag.
  useEffect(() => {
    if (!initialMessage) return
    if (initialMessageAppliedRef.current) return
    // Only seed the input if it's empty to avoid clobbering user typing.
    if (input) return
    setInput(initialMessage)
    initialMessageAppliedRef.current = true
  }, [initialMessage, input, setInput])

  // Auto-send trigger effect
  useEffect(() => {
    const shouldTriggerAutoSend =
      autoSendMessage &&
      !autoSendTriggeredRef.current &&
      input.trim() &&
      contextId &&
      !isChatLoading

    if (shouldTriggerAutoSend) {
      if (autoSendTimerRef.current) {
        clearTimeout(autoSendTimerRef.current)
      }

      autoSendTimerRef.current = setTimeout(() => {
        const content = input
        // Mark that auto-send is causing the reset to prevent re-triggering
        autoSendCausedResetRef.current = true
        autoSendTriggeredRef.current = true
        // Clear the textbox after auto-send so the user can immediately type a follow-up.
        setInput('')
        // Force new conversation - must call both startNewChat and resetChat
        // like handleNewChat does, so conversationIdRef is properly reset
        startNewChat()
        resetChat()
        send(content)
        autoSendTimerRef.current = null
      }, 300)
    }

    return () => {
      if (autoSendTimerRef.current) {
        clearTimeout(autoSendTimerRef.current)
        autoSendTimerRef.current = null
      }
    }
  }, [autoSendMessage, input, contextId, isChatLoading, send, startNewChat, resetChat, setInput])

  // Speak assistant messages when audio is enabled
  const lastMessageRef = useRef<string | null>(null)
  useEffect(() => {
    if (!audioEnabled || isChatLoading || messages.length === 0) return

    const lastMessage = messages[messages.length - 1]
    if (lastMessage.role === 'assistant' && lastMessage.id !== lastMessageRef.current) {
      // Extract text content from message parts
      const textContent = lastMessage.parts
        .filter((p) => p.type === 'text')
        .map((p) => (p as { type: 'text'; text: string }).text)
        .join(' ')

      if (textContent) {
        speak(textContent)
        lastMessageRef.current = lastMessage.id
      }
    }
  }, [audioEnabled, isChatLoading, messages, speak])

  // Handlers
  const handleNewChat = useCallback(() => {
    startNewChat()
    resetChat()
  }, [startNewChat, resetChat])

  // Keyboard shortcuts (after handlers so they're available)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in input
      const target = e.target as HTMLElement
      const isInputFocused = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA'

      // Escape to stop generating or close modal
      if (e.key === 'Escape') {
        if (showShortcutsModal) {
          setShowShortcutsModal(false)
        } else if (isChatLoading) {
          e.preventDefault()
          stop()
        }
        return
      }

      // Skip other shortcuts if typing in input (except Cmd+K)
      if (isInputFocused && !(e.metaKey || e.ctrlKey)) return

      // ? to show keyboard shortcuts
      if (e.key === '?' && !isInputFocused) {
        e.preventDefault()
        setShowShortcutsModal(true)
        return
      }

      // Cmd/Ctrl + K to start new chat
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        handleNewChat()
        return
      }

      // Cmd/Ctrl + / to toggle sidebar
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault()
        setIsSidebarCollapsed(!isSidebarCollapsed)
        return
      }

      // Cmd/Ctrl + ↑ to scroll to top
      if ((e.metaKey || e.ctrlKey) && e.key === 'ArrowUp') {
        e.preventDefault()
        const messagesContainer = document.querySelector('[data-messages-container]')
        messagesContainer?.scrollTo({ top: 0, behavior: 'smooth' })
        return
      }

      // Cmd/Ctrl + ↓ to scroll to bottom
      if ((e.metaKey || e.ctrlKey) && e.key === 'ArrowDown') {
        e.preventDefault()
        const messagesContainer = document.querySelector('[data-messages-container]')
        if (messagesContainer) {
          messagesContainer.scrollTo({ top: messagesContainer.scrollHeight, behavior: 'smooth' })
        }
        return
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isChatLoading, stop, showShortcutsModal, handleNewChat, isSidebarCollapsed, setIsSidebarCollapsed])

  const handleSendMessage = useCallback(
    (content: string, files?: File[]) => {
      send(content, files)
    },
    [send]
  )

  const handleAgentSelect = useCallback(
    (agentId: AgentId) => {
      setActiveAgent(agentId)
    },
    [setActiveAgent]
  )

  const handleQuickAction = useCallback(
    (action: string) => {
      // Prepend action to input or send directly
      const actionMessages: Record<string, string> = {
        calculate: t('chatv2:quick_actions.seed_calculate', { defaultValue: 'Help me calculate ' }),
        'show-triggers': t('chatv2:quick_actions.seed_show_triggers', { defaultValue: 'Show me the triggers for ' }),
        troubleshoot: t('chatv2:quick_actions.seed_troubleshoot', { defaultValue: 'Help me troubleshoot ' }),
        plan: t('chatv2:quick_actions.seed_plan', { defaultValue: 'Help me create a plan for ' }),
      }
      const message = actionMessages[action]
      if (message) {
        setInput(message)
      }
    },
    [setInput, t]
  )

  const handleFeedback = useCallback(
    async (messageId: string, rating: number, feedbackMessage?: string, screenshot?: string, userQuestion?: string, assistantAnswer?: string) => {
      if (!currentConversationId || !messageId) return

      await addMessageFeedback(currentConversationId, messageId, {
        rating,
        message: feedbackMessage,
        screenshot,
        userQuestion,
        assistantAnswer,
      })
      updateMessageFeedback(messageId, rating)
    },
    [currentConversationId, updateMessageFeedback]
  )

  const hasMessages = messages.length > 0

  // Error handler for ErrorBoundary
  const handleBoundaryError = useCallback((err: Error, errorInfo: React.ErrorInfo) => {
    console.error('[chat-boundary]', err, {
      componentStack: errorInfo.componentStack,
      contextId,
      contextType,
      conversationId: currentConversationId,
    })
  }, [contextId, contextType, currentConversationId])

  return (
    <ErrorBoundary onError={handleBoundaryError}>
      <ChatLayout
        isSidebarCollapsed={isSidebarCollapsed}
        onToggleSidebar={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        sidebar={
          <ChatSidebar
            conversations={filteredConversations}
            currentConversationId={currentConversationId}
            onNewChat={handleNewChat}
            onSelectConversation={selectConversation}
            onDeleteConversation={deleteConversation}
            onToggleSidebar={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            isCollapsed={isSidebarCollapsed}
            isLoading={isLoadingConversations || isSearching}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            searchStats={searchStats}
          />
        }
      >
        <div className="flex flex-col h-full" dir={getCurrentDirection()}>
          {/* Header with agent info and board selector */}
          <div className="flex-shrink-0 border-b border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card px-4 py-3">
            <div className="flex items-center justify-between gap-4">
              {/* Agent indicator */}
              <div className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: AGENTS[activeAgent]?.color === 'amber' ? '#D97706' :
                    AGENTS[activeAgent]?.color === 'blue' ? '#2563EB' :
                    AGENTS[activeAgent]?.color === 'green' ? '#16A34A' :
                    AGENTS[activeAgent]?.color === 'purple' ? '#9333EA' : '#D97706'
                  }}
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {AGENTS[activeAgent]?.label || t('chatv2:agents.pam.label', { defaultValue: 'AI Plant Support Engineer' })}
                </span>
              </div>

              {/* FS-663: share this conversation. Needs a saved conversation —
                  a brand-new unsent chat has no id to grant access to. */}
              {isFeatureAllowedInEmbed('chatShare') && currentConversationId && (
                <button
                  type="button"
                  onClick={() => setShowShareModal(true)}
                  aria-label={t('chatv2:header.share', { defaultValue: 'Share' })}
                  title={t('chatv2:header.share', { defaultValue: 'Share' })}
                  className="ml-auto mr-1 rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-dark-muted dark:hover:text-gray-200"
                >
                  <Share2 className="h-4 w-4" />
                </button>
              )}

              {/* Board selector */}
              {boardList && boardList.length > 0 && onBoardChange && (
                <Select value={contextId} onValueChange={onBoardChange}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder={t('chatv2:board.select', { defaultValue: 'Select board' })} />
                  </SelectTrigger>
                  <SelectContent>
                    {boardList.map((board) => (
                      <SelectItem key={board.id} value={board.id}>
                        <span className="block truncate max-w-[12rem]">
                          {board.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          {/* Main content area */}
          <div className="flex-1 overflow-hidden relative">
            {hasMessages || isLoadingMessages ? (
              <MessageList
                messages={messages}
                conversationId={currentConversationId}
                isLoading={isChatLoading}
                isLoadingMessages={isLoadingMessages}
                activeAgent={activeAgent}
                onFeedback={handleFeedback}
                onRegenerate={reload}
                onSendFollowUp={setInput}
                onEditMessage={editMessage}
                orchestrationStatus={orchestrationStatus}
                toolProgress={toolProgress}
                getVersionInfo={getVersionInfo}
                onNavigateVersion={navigateVersion}
                onConsentResponse={respondToConsent}
              />
            ) : (
              <WelcomeScreen
                activeAgent={activeAgent}
                onAgentSelect={handleAgentSelect}
                userName={userName}
                availableAgentIds={availableAgentIds}
              >
                {/* Input integrated into welcome screen */}
                <div className="w-full max-w-4xl mx-auto px-4 space-y-3">
                  <ChatInput
                    value={input}
                    onChange={setInput}
                    onSend={handleSendMessage}
                    onStop={stop}
                    isLoading={isChatLoading}
                    activeAgent={activeAgent}
                    onAgentChange={setActiveAgent}
                    availableAgentIds={availableAgentIds}
                    entireDataCatalog={entireDataCatalog}
                    onEntireDataCatalogChange={setEntireDataCatalog}
                    audioEnabled={audioEnabled}
                    onAudioToggle={setAudioEnabled}
                    isSpeaking={isSpeaking}
                    onStopSpeaking={stopSpeaking}
                    isSpeechAvailable={isSpeechAvailable}
                    onDataCatalog={() => setShowDocumentsModal(true)}
                    selectedDocuments={selectedDocsWithNames}
                    onRemoveDocument={handleRemoveDocument}
                    onShowAllDocuments={() => setShowDocumentsModal(true)}
                    onDocumentUploaded={handleDocumentUploaded}
                  />

                  <div className="flex justify-center gap-3 mt-4">
                    {QUICK_ACTIONS.map((action) => (
                      <button
                        key={action.id}
                        onClick={() => handleQuickAction(action.id)}
                        className="px-4 py-2 rounded-full border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-muted hover:border-gray-400 transition-colors"
                      >
                        {action.label}
                      </button>
                    ))}
                  </div>
                </div>
              </WelcomeScreen>
            )}
          </div>

          {/* Error display */}
          {error && (
            <div className="px-4 py-3 bg-red-50 dark:bg-red-900/20 border-t border-red-200 dark:border-red-800 flex items-center justify-between">
              <p className="text-sm text-red-600 dark:text-red-400">
                {getUserFriendlyError(error, t)}
              </p>
              <button
                onClick={reload}
                className="px-3 py-1 text-sm bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/40 text-red-700 dark:text-red-400 rounded transition-colors"
              >
                {t('chatv2:errors.retry', { defaultValue: 'Retry' })}
              </button>
            </div>
          )}

          {/* Input area - only when messages exist */}
          {(hasMessages || isLoadingMessages) && (
            <div className="flex-shrink-0 p-4 space-y-3">
              <ChatInput
                value={input}
                onChange={setInput}
                onSend={handleSendMessage}
                onStop={stop}
                isLoading={isChatLoading}
                activeAgent={activeAgent}
                onAgentChange={setActiveAgent}
                availableAgentIds={availableAgentIds}
                entireDataCatalog={entireDataCatalog}
                onEntireDataCatalogChange={setEntireDataCatalog}
                audioEnabled={audioEnabled}
                onAudioToggle={setAudioEnabled}
                isSpeaking={isSpeaking}
                onStopSpeaking={stopSpeaking}
                isSpeechAvailable={isSpeechAvailable}
                onDataCatalog={() => setShowDocumentsModal(true)}
                selectedDocuments={selectedDocsWithNames}
                onRemoveDocument={handleRemoveDocument}
                onShowAllDocuments={() => setShowDocumentsModal(true)}
                onDocumentUploaded={handleDocumentUploaded}
              />
            </div>
          )}
        </div>
      </ChatLayout>

      {/* Keyboard shortcuts modal */}
      <KeyboardShortcutsModal
        isOpen={showShortcutsModal}
        onClose={() => setShowShortcutsModal(false)}
      />

      {/* Network status indicator */}
      <NetworkStatus />

      {/* Add Data modals */}
      {/* FS-663: share this conversation. The chat is hosted by its board, so
          the link is /chat/<boardId>?c=<conversationId>. */}
      {isFeatureAllowedInEmbed('chatShare') && currentConversationId && (
        <ShareModal
          isOpen={showShareModal}
          setShow={setShowShareModal}
          target={{
            type: 'conversation',
            id: currentConversationId,
            boardId: contextId,
            // `Conversation` has no `title` — its human label is the opening
            // message (greptile/tsc on #27485). Trimmed so a long first message
            // does not become an unreadable dialog heading.
            name:
              conversations
                .find((c) => c.id === currentConversationId)
                ?.firstMessage?.slice(0, 60) ||
              t('chatv2:pending_share.title', { defaultValue: 'Chat' }),
          }}
        />
      )}

      <DocumentsModal
        isOpen={showDocumentsModal}
        onClose={() => setShowDocumentsModal(false)}
        onReopen={() => setShowDocumentsModal(true)}
      />
    </ErrorBoundary>
  )
}

export default ChatV2